/**
 * Service for working with image galleries
 * Contains functions for processing and preparing gallery data
 */
export default {
  /**
   * Prepare data for display in the gallery
   * @param {Object} options - Options for data preparation
   * @param {Array} options.images - Array of image URLs
   * @param {Array} options.titles - Array of titles
   * @param {Array} options.titleUrls - Array of URLs for titles
   * @param {Array} options.galleryTypes - Array of gallery types (profile-gallery, profile-selfie)
   * @param {Object} options.profile - Profile object (optional)
   * @param {Boolean} options.withComplement - Whether to complement main images with additional ones from another album
   * @returns {Object} - Object with data for ImgScreen
   */
  prepareGalleryData(options = {}) {
    const {
      images = [],
      titles = [],
      titleUrls = [],
      galleryTypes = [],
      sourceButtons = [],
      profile = null,
      withComplement = false,
    } = options;

    if (!images.length) {
      return {
        images: [],
        titles: [],
        titleUrls: [],
        galleryTypes: [],
        sourceButtons: [],
        imageTags: [],
      };
    }

    // Base result without additional images
    const result = {
      images,
      titles,
      titleUrls,
      galleryTypes,
      sourceButtons,
      imageTags: this.getImageTags(galleryTypes),
    };

    // If no need to complement with other images or no profile
    if (!withComplement || !profile) {
      console.log("GalleryService: one gallery", {
        withComplement,
        hasProfile: !!profile,
      });
      return result;
    }

    // Get the initial gallery type from the first image
    const initialGalleryType =
      galleryTypes[0] || this.detectGalleryType(images[0], profile);

    if (!initialGalleryType) {
      console.log("GalleryService: initial gallery type not found");
      return result;
    }

    const complementaryData = this.getComplementaryImages({
      profile,
      initialGalleryType,
    });

    // If no complementary images, return only base ones
    if (!complementaryData.images.length) {
      console.log("GalleryService: no additional images");
      return result;
    }

    // Add complementary data to the result
    const finalResult = {
      images: [...result.images, ...complementaryData.images],
      titles: [...result.titles, ...complementaryData.titles],
      titleUrls: [...result.titleUrls, ...complementaryData.titleUrls],
      galleryTypes: [...result.galleryTypes, ...complementaryData.galleryTypes],
      sourceButtons: [
        ...result.sourceButtons,
        ...complementaryData.sourceButtons,
      ],
      imageTags: [...result.imageTags, ...complementaryData.imageTags],
    };

    return finalResult;
  },

  /**
   * Attempts to determine the gallery type from image URL and profile
   * @param {string} imageUrl - Image URL
   * @param {Object} profile - Profile object
   * @returns {string|null} - Gallery type (profile-gallery, profile-selfie) or null
   */
  detectGalleryType(imageUrl, profile) {
    if (!profile || !imageUrl) return null;

    // Check if the image is in galleryFirst (selfie)
    if (profile.galleryFirst && Array.isArray(profile.galleryFirst)) {
      const foundInSelfie = profile.galleryFirst.some(
        (img) => img.src === imageUrl
      );
      if (foundInSelfie) return "profile-selfie";
    }

    // Check if the image is in gallerySecond (portfolio)
    if (profile.gallerySecond && Array.isArray(profile.gallerySecond)) {
      const foundInPortfolio = profile.gallerySecond.some(
        (img) => img.src === imageUrl
      );
      if (foundInPortfolio) return "profile-gallery";
    }

    return null;
  },

  /**
   * Get complementary images from the opposite album
   * @param {Object} options - Options for fetching complementary images
   * @param {Object} options.profile - Profile object
   * @param {String} options.initialGalleryType - Initial gallery type (profile-gallery, profile-selfie)
   * @returns {Object} - Object with complementary images data
   */
  getComplementaryImages({ profile, initialGalleryType }) {
    // Check all required parameters
    if (!profile || !initialGalleryType) {
      return {
        images: [],
        titles: [],
        titleUrls: [],
        galleryTypes: [],
        sourceButtons: [],
        imageTags: [],
      };
    }

    const galleryToUse =
      initialGalleryType === "profile-gallery"
        ? "galleryFirst"
        : "gallerySecond";

    // Check if this gallery exists in the profile
    if (!profile[galleryToUse] || !Array.isArray(profile[galleryToUse])) {
      return {
        images: [],
        titles: [],
        titleUrls: [],
        galleryTypes: [],
        sourceButtons: [],
        imageTags: [],
      };
    }

    const additionalImages = profile[galleryToUse].map((i) => i.src) || [];

    const complementaryType =
      initialGalleryType === "profile-gallery"
        ? "profile-selfie"
        : "profile-gallery";

    const result = {
      images: additionalImages,
      titles: additionalImages.map(() => ""),
      titleUrls: additionalImages.map(() => ""),
      galleryTypes: additionalImages.map(() => complementaryType),
      sourceButtons: additionalImages.map(() => false),
      imageTags: additionalImages.map(() =>
        complementaryType === "profile-gallery" ? "Portfolio" : "Natural/Selfie"
      ),
    };

    return result;
  },

  /**
   * Get tags for images based on gallery type
   * @param {Array} galleryTypes - Array of gallery types
   * @returns {Array} - Array of image tags
   */
  getImageTags(galleryTypes = []) {
    return galleryTypes.map((type) => {
      if (!type) return "";
      return type === "profile-gallery" ? "Portfolio" : "Natural/Selfie";
    });
  },
};
